import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'

// Layout
import Layout from './components/layout/Layout'

// Pages
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import StationUpdatePage from './pages/StationUpdatePage'
import SPKListPage from './pages/SPKListPage'
import SPKDetailPage from './pages/SPKDetailPage'
import SPKCreatePage from './pages/SPKCreatePage'
import SPKEditPage from './pages/SPKEditPage'
import QueuePage from './pages/QueuePage'
import WarehousePage from './pages/WarehousePage'
import SchoolsPage from './pages/SchoolsPage'
import VendorsPage from './pages/VendorsPage'
import RacksPage from './pages/RacksPage'
import MasterBukuPage from './pages/MasterBukuPage'
import UsersPage from './pages/UsersPage'
import RolesPage from './pages/RolesPage'
import ReportsPage from './pages/ReportsPage'
import EditQCHistoryPage from './pages/EditQCHistoryPage'
import OperatorsPage from './pages/OperatorsPage'
import ReviewerPage from './pages/ReviewerPage'
import FileRecapPage from './pages/FileRecapPage'
import UploadFilePage from './pages/UploadFilePage'
import SPKFilesPage from './pages/SPKFilesPage'
import ApiManagementPage from './pages/ApiManagementPage'
import ComcolorPage from './pages/ComcolorPage'
import NotFoundPage from './pages/NotFoundPage'

// Protected Route Component
const ProtectedRoute = ({ children, permission }) => {
  const { user, hasPermission } = useAuth()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" replace />
  }

  return children
}

function App() {
  const { user, loading } = useAuth()

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat aplikasi...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <LoginPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<DashboardPage />} />

        {/* Reviewer */}
        <Route
          path="reviewer"
          element={
            <ProtectedRoute permission="review_file">
              <ReviewerPage />
            </ProtectedRoute>
          }
        />

        {/* Upload File */}
        <Route
          path="upload-file"
          element={
            <ProtectedRoute permission={['files', 'spk']}>
              <UploadFilePage />
            </ProtectedRoute>
          }
        />

        {/* File Recap */}
        <Route
          path="file-recap"
          element={
            <ProtectedRoute permission={['files', 'review_file']}>
              <FileRecapPage />
            </ProtectedRoute>
          }
        />

        {/* Station Update */}
        <Route
          path="station"
          element={
            <ProtectedRoute permission="station_update">
              <StationUpdatePage />
            </ProtectedRoute>
          }
        />

        {/* Comcolor Operator */}
        <Route
          path="comcolor"
          element={
            <ProtectedRoute permission={['cetak_comcolor', 'station_update']}>
              <ComcolorPage />
            </ProtectedRoute>
          }
        />

        {/* Edit QC History */}
        <Route
          path="edit-qc"
          element={
            <ProtectedRoute permission="station_update">
              <EditQCHistoryPage />
            </ProtectedRoute>
          }
        />

        {/* Master Operators */}
        <Route
          path="operators"
          element={
            <ProtectedRoute permission="station_update">
              <OperatorsPage />
            </ProtectedRoute>
          }
        />

        {/* SPK Management */}
        <Route path="spk">
          <Route
            index
            element={
              <ProtectedRoute permission="spk">
                <SPKListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="create"
            element={
              <ProtectedRoute permission="spk">
                <SPKCreatePage />
              </ProtectedRoute>
            }
          />
          <Route path=":id" element={<SPKDetailPage />} />
          <Route
            path=":id/files"
            element={
              <ProtectedRoute permission={['files', 'spk']}>
                <SPKFilesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/edit"
            element={
              <ProtectedRoute permission="spk">
                <SPKEditPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Queue */}
        <Route
          path="queue"
          element={
            <ProtectedRoute permission="queue">
              <QueuePage />
            </ProtectedRoute>
          }
        />

        {/* Warehouse */}
        <Route
          path="warehouse"
          element={
            <ProtectedRoute permission="warehouse">
              <WarehousePage />
            </ProtectedRoute>
          }
        />

        {/* Master Data */}
        <Route
          path="schools"
          element={
            <ProtectedRoute permission="master_sekolah">
              <SchoolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute permission="vendor">
              <VendorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="racks"
          element={
            <ProtectedRoute permission="warehouse">
              <RacksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="books"
          element={
            <ProtectedRoute permission="spk_management">
              <MasterBukuPage />
            </ProtectedRoute>
          }
        />

        {/* User Management */}
        <Route
          path="users"
          element={
            <ProtectedRoute permission="users">
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="roles"
          element={
            <ProtectedRoute permission="roles">
              <RolesPage />
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="reports"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />

        {/* API Management */}
        <Route
          path="api-management"
          element={
            <ProtectedRoute permission="api_management">
              <ApiManagementPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
